import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../config/api';
import { getMatchLogos, getCompetitionLogo } from '../utils/teamLogos';
import { isActiveMemberForSeason } from '../utils/memberStatus';
import { formatMatchDateParts, formatMatchPhaseRange } from '../utils/matchDateTime';
import { getSalesPhaseTitle } from '../utils/salesPhaseTitle';
import InactiveMemberNotice from '../components/InactiveMemberNotice';
import PageMeta from '../components/PageMeta';
import { FaInfoCircle } from 'react-icons/fa';
import './Tickets.css';

const Tickets = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale, language } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderedTicketIds, setOrderedTicketIds] = useState(new Set());

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    const fetchMyOrders = async () => {
      if (!user) {
        setOrderedTicketIds(new Set());
        return;
      }
      try {
        const response = await api.get('/ticket-orders/my-orders');
        const ids = new Set(
          (response.data || [])
            .filter((order) => order.status !== 'cancelled' && order.ticket?._id)
            .map((order) => order.ticket._id),
        );
        setOrderedTicketIds(ids);
      } catch (error) {
        console.error('Error fetching ticket orders:', error);
        setOrderedTicketIds(new Set());
      }
    };
    fetchMyOrders();
  }, [user]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets');
      const now = new Date();
      const upcoming = (response.data || []).filter(
        (ticket) => ticket.date && new Date(ticket.date) > now
      );
      setTickets(upcoming);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateCard = (dateString) => formatMatchDateParts(dateString, locale);

  const formatPhaseRange = (startDate, endDate) => formatMatchPhaseRange(startDate, endDate, locale);

  const isPhaseActive = (phase) => {
    const now = new Date();
    const start = new Date(phase.startDate);
    const end = new Date(phase.endDate);
    return now >= start && now <= end;
  };

  const canOrderTickets = !user || isActiveMemberForSeason(user);

  const handleOrderClick = (ticket) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isActiveMemberForSeason(user)) {
      return;
    }
    navigate('/billets/paiement', { state: { ticket } });
  };

  return (
    <div className="tickets-page">
      <PageMeta
        title={t('seo.tickets.title')}
        description={t('seo.tickets.description')}
        path="/billets"
        locale={language}
      />
      <div className="container">
        <h1 className="page-title">{t('tickets.pageTitle')}</h1>

        <InactiveMemberNotice />

        {loading ? (
          <div className="loading">{t('tickets.loading')}</div>
        ) : (
          <>
            {tickets.length === 0 ? (
              <p className="no-tickets">{t('tickets.noTickets')}</p>
            ) : (
              <div className="tickets-grid tickets-grid-horizontal">
                {tickets.map((ticket) => {
                  const { homeLogo, awayLogo, homeTeam, awayTeam } = getMatchLogos(ticket.matchTitle);
                  const { dateLine, timeLine } = formatDateCard(ticket.date);
                  const alreadyOrdered = orderedTicketIds.has(ticket._id);
                  return (
                    <div key={ticket._id} className="ticket-card ticket-card-horizontal">
                      <div className="ticket-card-left">
                        <h3 className="ticket-card-title">{ticket.matchTitle}</h3>
                        <p className="ticket-card-date">{dateLine}</p>
                        <p className="ticket-card-time">{timeLine}</p>
                        {ticket.stadium && (
                          <p className="ticket-card-stadium">📍 {ticket.stadium}</p>
                        )}
                        <div className="ticket-card-actions">
                          <button
                            type="button"
                            className="btn-ticket-info"
                            onClick={() => {
                              if (!user) navigate('/login');
                              else navigate('/billets/paiement', { state: { ticket } });
                            }}
                          >
                            {t('tickets.infoPrice')}
                          </button>
                          <button
                            type="button"
                            className="btn-ticket-buy"
                            disabled={ticket.status === 'full' || alreadyOrdered}
                            onClick={() => {
                              if (!user) navigate('/login');
                              else navigate('/billets/paiement', { state: { ticket } });
                            }}
                          >
                            {alreadyOrdered
                              ? t('tickets.alreadyOrdered')
                              : ticket.status === 'full'
                                ? t('tickets.full')
                                : t('tickets.buy')}
                          </button>
                        </div>
                      </div>
                      <div className="ticket-card-center">
                        <div className="ticket-teams">
                          <div className="team-logo-container">
                            <img src={homeLogo} alt={homeTeam || 'Home'} className="team-logo" />
                            <span className="team-name">{homeTeam}</span>
                          </div>
                          <div className="vs-section">
                            {ticket.competition && getCompetitionLogo(ticket.competition) && (
                              <img
                                src={getCompetitionLogo(ticket.competition)}
                                alt={ticket.competition || 'Competition'}
                                className="competition-logo"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span className="vs-separator">VS</span>
                          </div>
                          <div className="team-logo-container">
                            <img src={awayLogo} alt={awayTeam || 'Away'} className="team-logo" />
                            <span className="team-name">{awayTeam}</span>
                          </div>
                        </div>
                      </div>
                      <div className="ticket-card-right">
                        <h4 className="sales-phases-heading">{t('tickets.salesPhases')}</h4>
                        {ticket.salesPhases && ticket.salesPhases.length > 0 ? (
                          <ul className="sales-phases-list">
                            {ticket.salesPhases.map((phase, index) => {
                              const active = isPhaseActive(phase);
                              return (
                                <li key={index} className={`sales-phase-item ${active ? 'active' : ''}`}>
                                  <span className="phase-name">{getSalesPhaseTitle(phase, language)}</span>
                                  <span className="phase-range">
                                    {formatPhaseRange(phase.startDate, phase.endDate)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="sales-phases-empty">{t('tickets.noPhases')}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <section className="help-section">
          <div className="container">
            <h2 className="section-title help-section-title">
              <FaInfoCircle className="help-section-icon" aria-hidden />
              {t('tickets.helpTitle')}
            </h2>
            <div className="help-content">
              <div className="help-block">
                <h3>{t('tickets.helpTicketShopTitle')}</h3>
                <p>
                  {t('tickets.helpTicketShopP1')}{' '}
                  <a href="https://tickets.juventus.com/it/calendar/subscription/fromHeader" target="_blank" rel="noopener noreferrer" className="help-link">
                    {t('tickets.helpTicketShopClick')}
                  </a>
                </p>
                <p>
                  {t('tickets.helpTicketShopP2')}
                </p>
                <p>
                  {t('tickets.helpTicketShopP3')}
                  <a href="https://tickets.juventus.com/it/calendar/authentication/?back=fromHeader" target="_blank" rel="noopener noreferrer" className="help-link">
                    {t('tickets.helpTicketShopClick')}
                  </a>
                </p>
                <p>
                  {t('tickets.helpTicketShopP4')}
                </p>
              </div>
              <div className="help-block">
                <h3>{t('tickets.helpFanTitle')}</h3>
                <p className="help-phone">
                  <a href="tel:+390114530486" className="help-link">+39 011 453 0486</a>
                </p>
                <p>
                  {t('tickets.helpFanP')}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Tickets;


